import { scoreMarkets } from "./market-score";
import type { Hip3Market } from "../types/hip3";

const COMMODITIES = new Set([
  "GOLD",
  "GOLDJM",
  "SILVER",
  "SILVERJM",
  "PLATINUM",
  "PALLADIUM",
  "COPPER",
  "ALUMINIUM",
  "URANIUM",
  "NATGAS",
  "GAS",
  "CL",
  "USOIL",
  "WTI",
  "OIL",
  "BRENTOIL",
]);

const FX = new Set(["EUR", "JPY", "GBP", "AUD", "CAD", "CHF", "NZD", "DXY"]);

const INDICES = new Set([
  "US500",
  "USA500",
  "USA100",
  "USTECH",
  "JP225",
  "JPN225",
  "KR200",
  "VIX",
  "USBOND",
  "USAR",
  "SMALL2000",
  "USENERGY",
  "US100",
  "XYZ100",
  "SEMI",
  "MAG7",
  "SEMIS",
  "ROBOT",
  "NUCLEAR",
  "ENERGY",
  "INFOTECH",
  "BIOTECH",
  "DEFENSE",
]);

const CRYPTO_PROVIDERS = new Set(["hyna"]);

const CRYPTO_ASSETS = new Set([
  "BTC",
  "ETH",
  "SOL",
  "XRP",
  "BNB",
  "DOGE",
  "ADA",
  "LTC",
  "BCH",
  "LINK",
  "XMR",
  "SUI",
  "ENA",
  "USDE",
  "PUMP",
  "PEPE",
  "FARTCOIN",
]);

export function normalizeMarketId(provider: string, marketId: string): string {
  const prefix = `${provider.trim()}:`;
  const normalized = marketId.trim();
  return normalized.startsWith(prefix) ? normalized.slice(prefix.length) : normalized;
}

export function marketSlug(market: Pick<Hip3Market, "provider" | "marketId">): string {
  return normalizeMarketId(market.provider, market.marketId);
}

export function classifyMarket(market: Hip3Market, providerAssets: Set<string>) {
  const asset = market.baseAsset.toUpperCase();
  if (FX.has(asset)) return "FX";
  if (COMMODITIES.has(asset)) return "Commodities";
  if (INDICES.has(asset)) return "Indices";
  if (CRYPTO_PROVIDERS.has(market.provider)) return "Crypto";
  if (CRYPTO_ASSETS.has(asset) || providerAssets.has(asset)) return "Crypto";
  if (/\d/.test(asset) && !INDICES.has(asset)) return "Crypto";
  return "Equities";
}

export function normalizeAndScoreMarkets(rawMarkets?: Hip3Market[]): Hip3Market[] {
  if (!rawMarkets?.length) {
    return [];
  }

  const cryptoFromProviders = new Set(
    rawMarkets
      .filter((market) => CRYPTO_PROVIDERS.has(market.provider))
      .map((market) => market.baseAsset.toUpperCase())
  );

  const normalizedMarkets = rawMarkets.map((market) => {
    const normalizedBase = market.baseAsset.toUpperCase();
    const normalizedQuote = market.quoteAsset.toUpperCase();

    const normalized: Hip3Market = {
      ...market,
      baseAsset: normalizedBase,
      quoteAsset: normalizedQuote,
      annualFunding:
        typeof market.funding === "number" && Number.isFinite(market.funding)
          ? market.funding * 24 * 365
          : undefined,
    };

    normalized.category =
      normalized.category ?? classifyMarket(normalized, cryptoFromProviders);

    return normalized;
  });

  return scoreMarkets(normalizedMarkets);
}

export function marketNotionalOi(market: Pick<Hip3Market, "openInterest" | "currentPrice">): number {
  const oi = typeof market.openInterest === "number" && Number.isFinite(market.openInterest)
    ? market.openInterest
    : 0;
  const price = typeof market.currentPrice === "number" && Number.isFinite(market.currentPrice)
    ? market.currentPrice
    : 0;
  return oi * price;
}

export function normalizeProvider(input: string): string {
  return input.trim().toLowerCase();
}

export function normalizeAsset(input: string): string {
  return input.trim().toUpperCase();
}
